import { packageRejectionMessage } from "./package-rejection-message";
import { ShippingTextError } from "@/lib/shipping-text";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import {
  Button,
  Card,
  CardBody,
  Chip,
  Input,
  Select,
  SelectItem,
  Spinner,
  Switch,
} from "@heroui/react";
import {
  AlertTriangle,
  Clock,
  Package,
  Sparkles,
  RefreshCw,
  Truck,
  Zap,
} from "lucide-react";
import { RadioDot, SlotCapacityLabel } from "../../sheet-primitives";
import { useCalculateQuote } from "@/api/use-shipment-quotes";
import { useSlotAvailability } from "@/api/use-slot-availability";
import { useOptionEstimates } from "@/api/use-option-estimates";
import {
  useAttachablePickupBookings,
  type AttachablePickupBooking,
} from "@/api/use-attachable-pickup-bookings";
import type {
  QuoteRequest,
  PricingOption,
  SelectedRate,
  PickupAddOn,
  SlotDeliveryEstimate,
  EstimateUnavailableReason,
} from "@/api.types.v2";
import { DropoffLocationList } from "../../dropoff-location-list";
import { useCreateOrder } from "../create-order-context";
import type { OrderDraft } from "../types";
import {
  formatDateInTz,
  formatSlotRangeInTz,
  isSlotBookable,
  todayIsoInTz,
  todayPlusDaysIsoInTz,
} from "@/lib/dispatch-time";
import { pickupSlotsForSelectedDate } from "../../pickup-date-availability";
import {
  formatAtCarrierRange,
  formatDeliveredRangeForTimeline,
  type DeliveryEstimateFormatOptions,
  formatOptionEstimateLine,
  formatSlotEstimateShort,
} from "@/lib/delivery-estimate-format";
import { isAxiosError } from "axios";
import { useSession } from "@/services/auth";
import { useTranslation } from "react-i18next";

const PICKUP_DATE_LOOKAHEAD_DAYS = 14;

export interface PricingStepHandle {
  submit: () => void;
}

interface PricingSelectionStepProps {
  data: OrderDraft;
  onNext: (data: Partial<OrderDraft>) => void;
  onEditDetails: (party: ShippingTextError["party"]) => void;
  onSelectionChange?: (
    optionKey: string | null,
    quote: PricingOption | null,
    pickupSchedule?: { pickupDate: string; pickupTimeSlotId: string }
  ) => void;
  onCanSubmitChange?: (canSubmit: boolean) => void;
}

function optionKey(opt: PricingOption): string {
  return `${opt.carrier.id}__${opt.carrierService.code}`;
}

function pickupSelectionKey(
  pricingOptionKey: string,
  pickupServiceId: string,
  pickupDate: string
): string {
  return `${pricingOptionKey}|${pickupServiceId}|${pickupDate}`;
}

function attachedBookingFitsOption(
  option: PricingOption | null,
  booking: AttachablePickupBooking
): boolean {
  return (
    option?.dispatch?.id === booking.dispatchCenter.id &&
    !!option.pickupOptions
      .find((tier) => tier.pickupServiceId === booking.pickupService.id)
      ?.timeSlots.some((slot) => slot.id === booking.slot.id)
  );
}

/**
 * Estimated timeline card — appears once a pickup slot is chosen. Walks the
 * package's journey: pickup window → (at carrier) → delivered. Every value is
 * a FORECAST and labelled as such.
 */
function DeliveryEstimateTimeline({
  estimate,
  carrierName,
  pickupDate,
  slotRange,
  dispatchTz,
}: {
  estimate: SlotDeliveryEstimate;
  carrierName: string;
  pickupDate: string;
  slotRange: string;
  dispatchTz: string;
}) {
  const { t, i18n } = useTranslation("app");
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const estimateFormat: DeliveryEstimateFormatOptions = {
    locale,
    t: (key, options) => t(key, options),
  };
  const atCarrier = formatAtCarrierRange(estimate, dispatchTz, estimateFormat);
  const delivered = formatDeliveredRangeForTimeline(
    estimate,
    dispatchTz,
    estimateFormat
  );

  const rows: { label: string; value: string }[] = [
    {
      label: t("createShipment.pickup"),
      value: `${formatDateInTz(
        pickupDate,
        dispatchTz,
        "EEE, MMM d",
        locale
      )}, ${slotRange}`,
    },
    ...(atCarrier
      ? [
          {
            label: t("createShipment.atCarrier", { carrier: carrierName }),
            value: atCarrier,
          },
        ]
      : []),
    {
      label: t("shipmentStatus.DELIVERED"),
      value: delivered,
    },
  ];

  return (
    <div className="rounded-large bg-content1 ring-1 ring-inset ring-divider overflow-hidden">
      <div className="px-5 py-3.5 border-b border-divider flex items-center gap-2">
        <Zap className="h-3.5 w-3.5 text-primary" />
        <span className="text-tiny font-semibold uppercase tracking-wider text-default-500">
          {t("createShipment.estimatedTimeline")}
        </span>
      </div>
      <div className="px-5 py-3.5 space-y-2.5">
        {rows.map((row, i) => (
          <div key={row.label} className="flex gap-3">
            <div className="flex flex-col items-center pt-1">
              <span
                className={`w-2 h-2 rounded-full ${
                  i === rows.length - 1 ? "bg-primary" : "bg-default-300"
                }`}
              />
              {i < rows.length - 1 && (
                <span className="w-px flex-1 bg-default-200 mt-1" />
              )}
            </div>
            <div className="pb-1 min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-default-500">
                {row.label}
              </div>
              <div className="text-[12.5px] text-default-900 font-medium mt-0.5">
                {row.value}
              </div>
            </div>
          </div>
        ))}
        <p className="text-[10px] text-default-400 pt-1 border-t border-divider">
          {t("createShipment.estimateDisclaimer", {
            suffix: estimate.atCarrier
              ? t("createShipment.carrierHoursSuffix")
              : "",
          })}
        </p>
      </div>
    </div>
  );
}

function browserTodayIso(): string {
  return new Date().toISOString().split("T")[0];
}

function PriceSummary({
  option,
  pickupCents,
  pickupDiscountCents,
  pickupTaxCents,
}: {
  option: PricingOption;
  pickupCents: number;
  /** Bundle discount earned by attaching to an existing run (display parity
   * with the charge, which applies it server-side at order placement). */
  pickupDiscountCents: number;
  pickupTaxCents: number;
}) {
  const { t } = useTranslation("app");
  const labelCents = option.labelPriceCents;
  const labelTaxCents = option.taxCents;
  const subtotalCents = labelCents + pickupCents - pickupDiscountCents;
  const taxCents = labelTaxCents + pickupTaxCents;
  const totalCents = subtotalCents + taxCents;
  const fmt = (cents: number) =>
    new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: option.currency,
    }).format(cents / 100);

  return (
    <div className="rounded-large bg-content1 ring-1 ring-inset ring-divider overflow-hidden">
      <div className="px-5 py-3.5 border-b border-divider">
        <div className="text-tiny font-semibold uppercase tracking-wider text-default-500">
          {t("createShipment.orderSummary")}
        </div>
      </div>
      <div className="px-5 py-3.5 space-y-2 text-sm">
        <div className="flex justify-between text-default-600">
          <span>{t("createShipment.shippingLabel")}</span>
          <span className="font-mono">{fmt(labelCents)}</span>
        </div>
        {pickupCents > 0 && (
          <div className="flex justify-between text-default-600">
            <span>{t("createShipment.pickup")}</span>
            <span className="font-mono">{fmt(pickupCents)}</span>
          </div>
        )}
        {pickupDiscountCents > 0 && (
          <div className="flex justify-between text-success">
            <span>{t("createShipment.pickupBundleDiscount")}</span>
            <span className="font-mono">−{fmt(pickupDiscountCents)}</span>
          </div>
        )}
        <div className="flex justify-between border-t border-divider pt-2 text-default-600">
          <span>{t("createShipment.subtotal")}</span>
          <span className="font-mono">{fmt(subtotalCents)}</span>
        </div>
        {taxCents > 0 && (
          <div className="flex justify-between text-default-600">
            <span>{t("createShipment.hst")}</span>
            <span className="font-mono">{fmt(taxCents)}</span>
          </div>
        )}
        <div className="flex justify-between border-t border-divider pt-2.5 items-baseline">
          <span className="text-default-900 font-semibold">
            {t("createShipment.total")}
          </span>
          <span className="font-mono text-lg font-bold tracking-tight">
            {fmt(totalCents)}
          </span>
        </div>
      </div>
    </div>
  );
}

export const PricingSelectionStep = forwardRef<
  PricingStepHandle,
  PricingSelectionStepProps
>(function PricingSelectionStep(
  { data, onNext, onEditDetails, onSelectionChange, onCanSubmitChange },
  ref
) {
  const { t, i18n } = useTranslation("app");
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const estimateFormat: DeliveryEstimateFormatOptions = {
    locale,
    t: (key, options) => t(key, options),
  };
  const estimateReasonText = (
    reason: EstimateUnavailableReason | null | undefined
  ) => t(`createShipment.deliveryUnavailable.${reason ?? "UNKNOWN"}`);
  const { login } = useSession();
  const { persistDraft } = useCreateOrder();
  const [draftSaveFailed, setDraftSaveFailed] = useState(false);
  const recoverSession = () => {
    if (persistDraft()) login();
    else setDraftSaveFailed(true);
  };
  const [selectedKey, setSelectedKey] = useState<string | null>(
    data.selectedQuote ? optionKey(data.selectedQuote) : null
  );
  const [includePickup, setIncludePickup] = useState<boolean>(
    data.includePickup ?? false
  );
  const [pickupDate, setPickupDate] = useState<string>(
    data.pickup?.scheduledDate ?? data.pickupDate ?? browserTodayIso()
  );
  const [pickupTimeSlotId, setPickupTimeSlotId] = useState<string>(
    data.pickup?.slotId ?? data.pickupTimeSlotId ?? ""
  );
  const [previousPickupSelectionKey, setPreviousPickupSelectionKey] = useState<
    string | null
  >(
    data.includePickup && data.selectedQuote
      ? pickupSelectionKey(
          optionKey(data.selectedQuote),
          data.pickup?.pickupServiceId ?? "",
          data.pickup?.scheduledDate ?? data.pickupDate ?? browserTodayIso()
        )
      : null
  );
  const [selectedPickupServiceId, setSelectedPickupServiceId] =
    useState<string>(data.pickup?.pickupServiceId ?? "");

  const { data: attachableData } = useAttachablePickupBookings({
    addressBookEntryId: data.shipFromAddressBookEntryId,
  });
  const attachableBookings = attachableData?.data ?? [];
  const [pickupMode, setPickupMode] = useState<"attach" | "new">(
    attachableBookings.length > 0 ? "attach" : "new"
  );
  const [selectedAttachableBookingId, setSelectedAttachableBookingId] =
    useState<string | undefined>(attachableBookings[0]?.id);

  const [prevAttachableLen, setPrevAttachableLen] = useState(-1);
  if (attachableBookings.length !== prevAttachableLen) {
    setPrevAttachableLen(attachableBookings.length);
    if (attachableBookings.length === 0) {
      setPickupMode("new");
      setSelectedAttachableBookingId(undefined);
    } else if (!selectedAttachableBookingId) {
      setSelectedAttachableBookingId(attachableBookings[0].id);
    }
  }

  const selectedAttachable: AttachablePickupBooking | undefined =
    attachableBookings.find((b) => b.id === selectedAttachableBookingId);

  const quoteRequest: QuoteRequest | null = useMemo(() => {
    if (!data.shipment) return null;
    const shipment = data.shipment;
    return {
      senderName: shipment.senderName,
      senderEmail: shipment.senderEmail,
      senderPhone: shipment.senderPhone,
      shipFrom: shipment.shipFrom,
      recipientName: shipment.recipientName,
      recipientEmail: shipment.recipientEmail,
      recipientPhone: shipment.recipientPhone,
      shipTo: shipment.shipTo,
      packages: shipment.packages,
      pickupNotes: shipment.pickupNotes,
      deliveryNotes: shipment.deliveryNotes,
    };
  }, [data.shipment]);

  const {
    data: quoteData,
    isPending,
    isFetching,
    isError,
    error,
    refetch,
  } = useCalculateQuote(quoteRequest);
  const isLoading = isPending || isFetching;

  const options = useMemo(() => quoteData?.data.options ?? [], [quoteData]);

  const selectedOption = useMemo(
    () =>
      selectedKey
        ? (options.find((o) => optionKey(o) === selectedKey) ?? null)
        : null,
    [selectedKey, options]
  );
  const withoutPickupAvailable =
    selectedOption?.estimates?.withoutPickup != null;

  const supportsPickup = (selectedOption?.pickupOptions?.length ?? 0) > 0;

  const [prevOptionId, setPrevOptionId] = useState<string | null>(null);
  const currentOptionId = selectedOption?.carrier.id ?? null;
  if (currentOptionId !== prevOptionId) {
    setPrevOptionId(currentOptionId);
    if (selectedOption && !supportsPickup && includePickup) {
      setIncludePickup(false);
    }
  }

  const tierKey = (selectedOption?.pickupOptions ?? [])
    .map((p) => p.pickupServiceId)
    .join("|");
  const [prevTierKey, setPrevTierKey] = useState<string>("");
  if (tierKey !== prevTierKey) {
    setPrevTierKey(tierKey);
    const tiers = selectedOption?.pickupOptions ?? [];
    if (tiers.length === 0) {
      setSelectedPickupServiceId("");
    } else if (
      !tiers.some((t) => t.pickupServiceId === selectedPickupServiceId)
    ) {
      setSelectedPickupServiceId(tiers[0].pickupServiceId);
    }
  }

  const activePickupServiceId =
    pickupMode === "attach" && selectedAttachable
      ? selectedAttachable.pickupService.id
      : selectedPickupServiceId;
  const selectedTier = selectedOption?.pickupOptions.find(
    (tier) => tier.pickupServiceId === activePickupServiceId
  );
  const attachedBookingMatchesOption =
    pickupMode !== "attach" ||
    !selectedAttachable ||
    attachedBookingFitsOption(selectedOption, selectedAttachable);

  const dispatchTz = selectedOption?.dispatch?.timeZone ?? null;
  const dispatchCity = selectedOption?.dispatch?.city ?? null;
  const todayIso = dispatchTz
    ? todayIsoInTz(dispatchTz)
    : new Date().toISOString().split("T")[0];
  const maxDateIso = dispatchTz
    ? todayPlusDaysIsoInTz(dispatchTz, PICKUP_DATE_LOOKAHEAD_DAYS)
    : null;

  const baseBookableSlots = useMemo(() => {
    if (!selectedTier) return [];
    if (!dispatchTz) return selectedTier.timeSlots;
    return selectedTier.timeSlots.filter((s) =>
      isSlotBookable(pickupDate, s, dispatchTz)
    );
  }, [selectedTier, dispatchTz, pickupDate]);

  // Quote slots carry no capacity (date unknown at quote time). Fetch
  // remaining for the chosen date and merge it in so the tiles can show
  // "N left" / "Full" — same treatment as the schedule-pickup wizard.
  // Also recomputes delivery estimates for the chosen date (the quote's
  // per-slot estimates assume its earliest bookable date).
  const effectivePickupDate = !includePickup
    ? ""
    : pickupMode === "attach"
      ? (selectedAttachable?.scheduledDate.slice(0, 10) ?? "")
      : pickupDate;
  const slotIdsForPlan = !includePickup
    ? []
    : pickupMode === "attach"
      ? attachedBookingMatchesOption && selectedAttachable
        ? [selectedAttachable.slot.id]
        : []
      : baseBookableSlots.map((slot) => slot.id);
  const {
    data: slotAvailability,
    isFetching: slotAvailabilityFetching,
    isError: slotAvailabilityError,
  } = useSlotAvailability(
    slotIdsForPlan,
    effectivePickupDate,
    selectedOption?.carrierService.id
  );
  const noLocalSlots =
    includePickup && pickupMode === "new" && baseBookableSlots.length === 0;
  const slotAvailabilityReady = Boolean(
    dispatchTz &&
      !slotAvailabilityFetching &&
      (slotAvailability || slotAvailabilityError || noLocalSlots)
  );
  const bookableSlots = useMemo(() => {
    if (
      !dispatchTz ||
      !slotAvailability ||
      slotAvailabilityFetching ||
      slotAvailabilityError
    ) {
      return [];
    }
    return pickupSlotsForSelectedDate(
      baseBookableSlots.map((s) => ({
        ...s,
        remaining: slotAvailability?.remaining?.[s.id] ?? s.remaining ?? null,
        availabilityStatus: slotAvailability?.statuses?.[s.id] ?? null,
        estimate: slotAvailability?.estimates?.[s.id] ?? null,
        estimateReason: slotAvailability?.estimateReasons?.[s.id] ?? null,
      })),
      pickupDate,
      dispatchTz
    );
  }, [
    baseBookableSlots,
    slotAvailability,
    slotAvailabilityFetching,
    slotAvailabilityError,
    pickupDate,
    dispatchTz,
  ]);

  const pickupAvailabilityKey =
    includePickup && pickupMode === "new"
      ? pickupSelectionKey(
          selectedKey ?? "",
          selectedPickupServiceId,
          pickupDate
        )
      : null;
  if (
    pickupAvailabilityKey &&
    pickupAvailabilityKey !== previousPickupSelectionKey
  ) {
    setPreviousPickupSelectionKey(pickupAvailabilityKey);
    setPickupTimeSlotId("");
  }

  // The selected plan uses its exact slot, including an attached booking. A
  // quote-time estimate for another date must never stand in for this response.
  const selectedSlotEstimate = useMemo(() => {
    if (!includePickup || !slotAvailabilityReady || slotAvailabilityError)
      return null;
    const slotId =
      pickupMode === "attach" ? selectedAttachable?.slot.id : pickupTimeSlotId;
    const estimate = slotId ? slotAvailability?.estimates?.[slotId] : null;
    return estimate?.forDate === effectivePickupDate ? estimate : null;
  }, [
    includePickup,
    slotAvailabilityReady,
    slotAvailabilityError,
    pickupMode,
    selectedAttachable,
    pickupTimeSlotId,
    slotAvailability,
    effectivePickupDate,
  ]);

  // Date-aware estimates for EVERY option card on the chosen pickup date
  // (selectedSlotEstimate above only covers the selected option's exact slot).
  // Attach mode anchors on the existing booking's date; new mode on the picked
  // date; pickup off → no fetch (cards fall back to the without-pickup line).
  const optionEstimateInputs = useMemo(
    () =>
      effectivePickupDate
        ? options
            .map((opt) => ({
              key: optionKey(opt),
              carrierServiceId: opt.carrierService.id,
              handoffDispatchCenterId:
                opt.estimates?.withoutPickup?.dispatchCenterId,
              slotIds:
                (pickupMode !== "attach" ||
                (selectedAttachable &&
                  attachedBookingFitsOption(opt, selectedAttachable))
                  ? opt.pickupOptions
                      .find(
                        (tier) => tier.pickupServiceId === activePickupServiceId
                      )
                      ?.timeSlots.filter((slot) =>
                        pickupMode === "attach"
                          ? slot.id === selectedAttachable?.slot.id
                          : !pickupTimeSlotId || slot.id === pickupTimeSlotId
                      )
                      .map((slot) => slot.id)
                  : []) ?? [],
            }))
            .filter((option) => option.slotIds.length > 0)
        : [],
    [
      options,
      effectivePickupDate,
      activePickupServiceId,
      pickupMode,
      selectedAttachable,
      pickupTimeSlotId,
    ]
  );
  const {
    data: optionEstimateData,
    isFetching: optionEstimatesFetching,
    isError: optionEstimatesError,
  } = useOptionEstimates(effectivePickupDate, optionEstimateInputs);

  const [prevTodayIso, setPrevTodayIso] = useState<string>("");
  if (dispatchTz && todayIso !== prevTodayIso) {
    setPrevTodayIso(todayIso);
    if (pickupDate < todayIso) {
      setPickupDate(todayIso);
    }
  }

  const selectableSlots = bookableSlots.filter(
    (slot) =>
      slot.availabilityStatus === "AVAILABLE" &&
      slot.remaining !== 0 &&
      slot.estimate !== null
  );
  const attachedPickupIssue =
    includePickup && pickupMode === "attach" && selectedAttachable
      ? !selectedTier || !attachedBookingMatchesOption
        ? t("createShipment.attachedPickupUnavailable")
        : slotAvailabilityError
          ? t("createShipment.deliveryCheckFailed")
          : slotAvailabilityReady && !selectedSlotEstimate
            ? estimateReasonText(
                slotAvailability?.estimateReasons?.[selectedAttachable.slot.id]
              )
            : null
      : null;
  const bookableKey = selectableSlots.map((s) => s.id).join("|");
  useEffect(() => {
    if (
      !pickupAvailabilityKey ||
      !slotAvailabilityReady ||
      slotAvailabilityError
    )
      return;
    const selectableSlotIds = bookableKey ? bookableKey.split("|") : [];
    setPickupTimeSlotId((current) =>
      selectableSlotIds.includes(current)
        ? current
        : selectableSlotIds.length === 1
          ? selectableSlotIds[0]
          : ""
    );
  }, [
    pickupAvailabilityKey,
    slotAvailabilityReady,
    slotAvailabilityError,
    bookableKey,
  ]);

  useEffect(() => {
    if (!onSelectionChange) return;
    if (!selectedOption) {
      onSelectionChange(null, null);
      return;
    }
    const schedule = !includePickup
      ? undefined
      : pickupMode === "attach" && selectedAttachable
        ? {
            pickupDate: selectedAttachable.scheduledDate.slice(0, 10),
            pickupTimeSlotId: selectedAttachable.slot.id,
          }
        : pickupDate && pickupTimeSlotId
          ? { pickupDate, pickupTimeSlotId }
          : undefined;
    onSelectionChange(selectedKey, selectedOption, schedule);
  }, [
    selectedKey,
    selectedOption,
    includePickup,
    pickupMode,
    selectedAttachable,
    pickupDate,
    pickupTimeSlotId,
    onSelectionChange,
  ]);

  const canSubmit =
    !isLoading &&
    !isError &&
    !!quoteData?.data.addressesInServiceArea &&
    !!selectedOption &&
    !!selectedKey &&
    (!includePickup
      ? withoutPickupAvailable
      : pickupMode === "new"
        ? !!pickupDate &&
          !!pickupTimeSlotId &&
          slotAvailabilityReady &&
          !!selectedSlotEstimate &&
          selectableSlots.some((slot) => slot.id === pickupTimeSlotId)
        : !!selectedAttachable &&
          !!selectedTier &&
          attachedBookingMatchesOption &&
          slotAvailabilityReady &&
          !!selectedSlotEstimate);

  useEffect(() => {
    onCanSubmitChange?.(canSubmit);
  }, [canSubmit, onCanSubmitChange]);

  const handleContinue = () => {
    if (!canSubmit || !selectedKey || !selectedOption) return;

    const effectivePickupDate =
      includePickup && pickupMode === "attach" && selectedAttachable
        ? new Date(selectedAttachable.scheduledDate).toISOString().split("T")[0]
        : pickupDate;
    const effectivePickupSlotId =
      includePickup && pickupMode === "attach" && selectedAttachable
        ? selectedAttachable.slot.id
        : pickupTimeSlotId;

    if (includePickup && (!effectivePickupDate || !effectivePickupSlotId))
      return;

    // The id, not the price. The server bills the quote row it wrote — see
    // SelectedRate. `labelPriceCents` is display-only and must never be echoed.
    const selectedRate: SelectedRate = {
      quoteId: selectedOption.quoteId,
      carrierId: selectedOption.carrier.id,
      carrierServiceCode: selectedOption.carrierService.code,
      fromServiceAreaId: selectedOption.fromServiceAreaId,
      toServiceAreaId: selectedOption.toServiceAreaId,
      currency: selectedOption.currency,
    };

    let pickup: PickupAddOn | undefined;
    // DISPLAY ONLY — it rides on the draft, not inside `pickup`, because `pickup` is
    // the request body. The server re-derives the bundle discount at placement.
    let pickupBundleDiscountCents = 0;
    if (includePickup) {
      let effectivePickupServiceId: string | undefined;
      if (pickupMode === "attach" && selectedAttachable) {
        effectivePickupServiceId = selectedAttachable.pickupService.id;
      } else if (pickupMode === "new" && selectedPickupServiceId) {
        effectivePickupServiceId = selectedPickupServiceId;
      }
      if (effectivePickupServiceId) {
        const tier = selectedOption?.pickupOptions.find(
          (t) => t.pickupServiceId === effectivePickupServiceId
        );
        if (tier) {
          // Bundle discount for attaching to an existing run — carried through
          // the wizard so the payment step shows the same (discounted) total
          // the server will actually charge.
          const bundleFraction =
            pickupMode === "attach"
              ? (selectedAttachable?.nextBundleFractionOff ?? 0)
              : 0;
          pickupBundleDiscountCents = Math.round(
            tier.pickupPriceCents * bundleFraction
          );
          // The TIER the customer picked. Not what it costs — that is the quote's to
          // say, and the server reads it off the leg it wrote.
          pickup = {
            pickupServiceId: tier.pickupServiceId,
            scheduledDate: effectivePickupDate,
            slotId: effectivePickupSlotId,
          };
        }
      }
    }

    onNext({
      selectedQuote: selectedOption,
      includePickup,
      selectedRate,
      pickup,
      pickupBundleDiscountCents,
    });
  };

  useImperativeHandle(ref, () => ({ submit: handleContinue }), [
    handleContinue,
  ]);

  const formatCurrency = (cents: number) =>
    new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
    }).format(cents / 100);

  const formatDeliveryTime = (days?: number) => {
    if (days == null) return t("createShipment.contactForDetails");
    if (days === 0) return t("createShipment.sameDay");
    if (days === 1) return t("createShipment.nextDay");
    return t("createShipment.businessDays", { count: days });
  };

  const { cheapestOption, fastestOption } = useMemo((): {
    cheapestOption: PricingOption | null;
    fastestOption: PricingOption | null;
  } => {
    if (options.length === 0)
      return { cheapestOption: null, fastestOption: null };
    const cheapest = [...options].sort(
      (a, b) => a.labelPriceCents - b.labelPriceCents
    )[0];
    const fastest = [...options].sort(
      (a, b) =>
        (a.carrierService.estimatedDays ?? 99) -
        (b.carrierService.estimatedDays ?? 99)
    )[0];
    return { cheapestOption: cheapest, fastestOption: fastest };
  }, [options]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <Spinner size="md" />
        <p className="text-sm text-default-500">
          {t("createShipment.calculatingOptions")}
        </p>
      </div>
    );
  }

  if (isError) {
    const status = isAxiosError(error) ? error.response?.status : undefined;
    const authenticationRequired = status === 401;
    const message =
      error instanceof ShippingTextError
        ? "validation.shippingText"
        : authenticationRequired
          ? "createShipment.pricingSignInRequired"
          : status === 400 || status === 422
            ? "createShipment.pricingInvalidDetails"
            : "createShipment.pricingLoadError";
    return (
      <Card>
        <CardBody className="py-6 text-center items-center gap-3">
          <p role="alert" className="text-sm">
            {error instanceof ShippingTextError
              ? error.localizedMessage(t)
              : t(message)}
          </p>
          {draftSaveFailed && (
            <p role="alert">{t("createShipment.draftSaveFailed")}</p>
          )}
          <Button
            onPress={
              error instanceof ShippingTextError
                ? () => onEditDetails(error.party)
                : authenticationRequired
                  ? recoverSession
                  : () => void refetch()
            }
          >
            {t(
              error instanceof ShippingTextError
                ? error.party === "Sender"
                  ? "createShipment.editSenderDetails"
                  : "createShipment.editRecipientDetails"
                : authenticationRequired
                  ? "createShipment.signInAgain"
                  : "createShipment.refresh"
            )}
          </Button>
        </CardBody>
      </Card>
    );
  }

  if (!quoteData?.data.addressesInServiceArea) {
    const reason =
      quoteData?.data.reason ?? t("createShipment.serviceAreaDefaultReason");
    const suggestion = quoteData?.data.suggestion;
    return (
      <Card>
        <CardBody className="py-6 text-center space-y-3">
          <p className="text-sm">{reason}</p>
          {suggestion && (
            <p className="text-xs text-default-500">{suggestion}</p>
          )}
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Package className="h-5 w-5" />
            {t("createShipment.shippingOptions")}
          </h3>
          <p className="text-sm text-default-500 mt-1">
            {t("createShipment.shippingOptionsLede")}
          </p>
        </div>
        <Button
          variant="bordered"
          size="sm"
          onPress={() => void refetch()}
          isDisabled={isLoading || !quoteRequest}
          startContent={
            <RefreshCw
              className={`h-3 w-3 ${isLoading ? "animate-spin" : ""}`}
            />
          }
          className="flex-shrink-0"
        >
          {t("createShipment.refresh")}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 flex flex-col gap-2.5">
          {quoteData?.data.rejectionReasons &&
            quoteData.data.rejectionReasons.length > 0 && (
              <div
                role="status"
                className="bg-content1 rounded-large shadow-small p-4 text-xs text-default-500 space-y-1"
              >
                <p className="font-medium">
                  {t("createShipment.unavailableOptions")}
                </p>
                {Array.from(
                  new Map(
                    quoteData.data.rejectionReasons.map((r) => [
                      `${r.serviceId ?? r.carrierId}:${r.reason}`,
                      r,
                    ])
                  ).values()
                ).map((r) => (
                  <div key={`${r.serviceId ?? r.carrierId}:${r.reason}`}>
                    {r.serviceName && (
                      <p className="font-medium">{r.serviceName}</p>
                    )}
                    <span>
                      {packageRejectionMessage(
                        r,
                        data.shipment?.packages ?? [],
                        t
                      )}
                    </span>
                    {(r.code === "PACKAGE_WEIGHT_UNPRICED" ||
                      r.code === "PACKAGE_EXCEEDS_LIMITS") && (
                      <span className="block text-[11px] opacity-80">
                        {t("createShipment.packageWeightSuggestion")}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          {options.length === 0 ? (
            <div className="bg-content1 rounded-large shadow-small py-8 text-center text-sm space-y-2">
              <p className="text-default-500">
                {t("createShipment.noOptions")}
              </p>
            </div>
          ) : (
            options.map((opt) => {
              const key = optionKey(opt);
              const isSelected = selectedKey === key;
              const isCheapest =
                cheapestOption && key === optionKey(cheapestOption);
              const isFastest =
                fastestOption && key === optionKey(fastestOption);
              const total = opt.labelPriceCents;

              const badge = isFastest
                ? {
                    kind: "fastest" as const,
                    label: t("createShipment.fastest"),
                    color: "default" as const,
                  }
                : isCheapest
                  ? {
                      kind: "cheapest" as const,
                      label: t("createShipment.cheapest"),
                      color: "success" as const,
                    }
                  : null;

              return (
                <div
                  key={key}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedKey(key)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setSelectedKey(key);
                    }
                  }}
                  className={`relative flex items-center gap-3 px-4 py-3 rounded-medium cursor-pointer transition-all bg-content1 ${
                    isSelected
                      ? "ring-[1.5px] ring-inset ring-primary bg-primary-50"
                      : "ring-1 ring-inset ring-divider hover:ring-default-300"
                  }`}
                >
                  {isFastest && (
                    <span className="absolute -top-2 left-4 px-2 py-px bg-default-900 text-primary-foreground rounded text-[9px] font-bold tracking-[0.08em]">
                      {t("createShipment.recommended")}
                    </span>
                  )}
                  <RadioDot selected={isSelected} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-default-500">
                      {opt.carrier.name}
                    </div>
                    <div className="text-[14px] font-bold text-default-900 leading-tight mt-0.5">
                      {opt.carrierService.name}
                    </div>
                    <div className="text-[11.5px] text-default-600 mt-1 inline-flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDeliveryTime(opt.carrierService.estimatedDays)}
                    </div>
                    {/* Delivery estimate (forecast) — adapts to the pickup
                        toggle; absent entirely when config is incomplete. */}
                    {(() => {
                      const tz = opt.dispatch?.timeZone ?? null;
                      const dateAware =
                        optionEstimateData?.estimates[key] ?? null;
                      const matchingTier = opt.pickupOptions.find(
                        (tier) => tier.pickupServiceId === activePickupServiceId
                      );
                      const matchingAttachedBooking =
                        pickupMode !== "attach" ||
                        (!!selectedAttachable &&
                          attachedBookingFitsOption(opt, selectedAttachable));
                      const canEstimateOption = optionEstimateInputs.some(
                        (input) => input.key === key
                      );
                      const checking =
                        canEstimateOption &&
                        (!optionEstimateData || optionEstimatesFetching);
                      const estimateLine = !includePickup
                        ? (formatOptionEstimateLine(
                            opt.estimates,
                            false,
                            tz,
                            estimateFormat
                          ) ??
                          (opt.estimateReasons?.withoutPickup
                            ? estimateReasonText(
                                opt.estimateReasons.withoutPickup
                              )
                            : t("createShipment.withoutPickupUnavailable")))
                        : !matchingTier
                          ? t("createShipment.pickupTierUnavailable")
                          : !matchingAttachedBooking
                            ? t("createShipment.attachedPickupUnavailable")
                            : isSelected && selectedSlotEstimate
                              ? formatSlotEstimateShort(
                                  selectedSlotEstimate,
                                  tz ?? "America/Toronto",
                                  estimateFormat
                                )
                              : !canEstimateOption
                                ? pickupTimeSlotId && pickupMode === "new"
                                  ? t("createShipment.pickupSlotUnavailable")
                                  : estimateReasonText("NO_BOOKABLE_SLOT")
                                : optionEstimatesError
                                  ? t("createShipment.deliveryCheckFailed")
                                  : checking
                                    ? t("createShipment.checkingDelivery")
                                    : dateAware?.withPickup
                                      ? formatOptionEstimateLine(
                                          dateAware,
                                          true,
                                          tz,
                                          estimateFormat
                                        )
                                      : estimateReasonText(
                                          optionEstimateData?.estimateReasons?.[
                                            key
                                          ]?.withPickup
                                        );
                      if (!estimateLine) return null;
                      return (
                        <div
                          className={`flex items-start gap-1 text-[11px] font-medium mt-1.5 ${
                            includePickup &&
                            (
                              !matchingTier ||
                                !matchingAttachedBooking ||
                                !canEstimateOption ||
                                optionEstimatesError ||
                                (!checking &&
                                  !dateAware?.withPickup &&
                                  !(isSelected && selectedSlotEstimate))
                            )
                              ? "text-danger-600"
                              : "text-primary"
                          }`}
                        >
                          <Zap className="h-3 w-3 flex-shrink-0 mt-0.5" />
                          <span className="min-w-0">{estimateLine}</span>
                        </div>
                      );
                    })()}
                  </div>

                  <div className="text-right flex-shrink-0 flex items-center gap-2">
                    {badge && (
                      <Chip
                        size="sm"
                        variant="flat"
                        color={badge.color}
                        startContent={
                          badge.kind === "fastest" ? (
                            <Clock className="h-2.5 w-2.5" />
                          ) : (
                            <Sparkles className="h-2.5 w-2.5" />
                          )
                        }
                        className="h-[20px] text-[10px]"
                      >
                        {badge.label}
                      </Chip>
                    )}
                    <div>
                      <div className="text-[18px] font-bold font-mono leading-none tracking-[-0.02em]">
                        {formatCurrency(total)}
                      </div>
                      <div className="text-[10px] text-default-500 mt-0.5">
                        {opt.currency} · {t("createShipment.preTax")}
                      </div>
                      {opt.carrierDiscount?.appliedDiscountCents != null &&
                        opt.carrierDiscount.appliedDiscountCents > 0 && (
                          <div className="text-[10px] text-success-600 mt-1">
                            {t("createShipment.carrierDiscountIncluded", {
                              amount: formatCurrency(
                                opt.carrierDiscount.appliedDiscountCents
                              ),
                            })}
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <aside className="lg:col-span-2 flex flex-col gap-3">
          <div className="rounded-large bg-content1 ring-1 ring-inset ring-divider overflow-hidden">
            <div
              className={`px-5 py-4 flex items-center gap-3 ${
                includePickup ? "border-b border-divider" : ""
              }`}
            >
              <div className="w-[34px] h-[34px] rounded-[9px] bg-primary-50 text-primary grid place-items-center flex-shrink-0">
                <Truck className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold">
                  {t("createShipment.pickupAddOn")}
                </div>
                <div className="text-xs text-default-500">
                  {t("createShipment.optionalOnce")}
                </div>
              </div>
              {supportsPickup && selectedOption && (
                <Switch
                  isSelected={includePickup}
                  onValueChange={setIncludePickup}
                />
              )}
            </div>

            <div className="p-5 space-y-3">
              {!selectedOption ? (
                <p className="text-xs text-default-500">
                  {t("createShipment.pickOptionForPickup")}
                </p>
              ) : !supportsPickup ? (
                <p className="text-xs text-default-500">
                  {selectedOption.pickupRejectionReasons?.some(
                    (r) => r.code === "PACKAGE_WEIGHT_UNPRICED"
                  )
                    ? t("createShipment.pickupPackageWeightUnpriced")
                    : t("createShipment.pickupUnavailable")}
                </p>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-2">
                    <div className="space-y-0.5 min-w-0">
                      <div className="text-sm font-medium">
                        {includePickup
                          ? t("createShipment.pickupAtSender")
                          : t("createShipment.addPickupAtSender")}
                      </div>
                      <p className="text-xs text-default-500">
                        {selectedOption.pickupOptions.length > 1
                          ? t("createShipment.fromPrice", {
                              amount: formatCurrency(
                                Math.min(
                                  ...selectedOption.pickupOptions.map(
                                    (t) => t.pickupPriceCents
                                  )
                                )
                              ),
                            })
                          : `+${formatCurrency(
                              selectedOption.pickupOptions[0]
                                ?.pickupPriceCents ?? 0
                            )}`}
                      </p>
                    </div>
                  </div>

                  {!includePickup && selectedOption && (
                    <div className="pt-3 border-t">
                      <p className="text-sm font-medium text-primary">
                        {t("createShipment.dropOffAt", {
                          carrier: selectedOption.carrier.name,
                        })}
                      </p>
                      <DropoffLocationList
                        carrierId={selectedOption.carrier.id}
                      />
                    </div>
                  )}

                  {includePickup && (
                    <div className="space-y-3 pt-3 border-t">
                      {attachableBookings.length > 0 && (
                        <div className="space-y-2">
                          <label className="flex items-start gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="pickupMode"
                              className="mt-1"
                              checked={pickupMode === "attach"}
                              onChange={() => setPickupMode("attach")}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium">
                                {t("createShipment.attachExistingPickup")}
                              </div>
                              {pickupMode === "attach" &&
                                selectedAttachable && (
                                  <div className="mt-2 rounded-md border bg-default-50 p-3 space-y-2">
                                    <div className="text-sm font-medium">
                                      {formatDateInTz(
                                        selectedAttachable.scheduledDate.slice(
                                          0,
                                          10
                                        ),
                                        selectedAttachable.dispatchCenter
                                          .timeZone,
                                        "EEE, MMM d",
                                        locale
                                      )}
                                      , {selectedAttachable.slot.name} (
                                      {formatSlotRangeInTz(
                                        selectedAttachable.slot,
                                        selectedAttachable.dispatchCenter
                                          .timeZone,
                                        selectedAttachable.scheduledDate.slice(
                                          0,
                                          10
                                        ),
                                        locale
                                      )}
                                      )
                                    </div>
                                    <div className="text-xs text-default-500">
                                      {t("createShipment.shipmentsOnRun", {
                                        count:
                                          selectedAttachable.attachedShipmentCount,
                                      })}
                                    </div>
                                    {selectedSlotEstimate ? (
                                      <p className="text-xs font-medium text-primary">
                                        {formatSlotEstimateShort(
                                          selectedSlotEstimate,
                                          selectedAttachable.dispatchCenter
                                            .timeZone,
                                          estimateFormat
                                        )}
                                      </p>
                                    ) : attachedPickupIssue ? (
                                      <div role="alert" className="space-y-2">
                                        <p className="text-xs text-danger-700">
                                          {attachedPickupIssue}
                                        </p>
                                        {withoutPickupAvailable ? (
                                          <Button
                                            size="sm"
                                            variant="bordered"
                                            onPress={() =>
                                              setIncludePickup(false)
                                            }
                                          >
                                            {t(
                                              "createShipment.continueWithoutPickup"
                                            )}
                                          </Button>
                                        ) : (
                                          <p className="text-xs text-default-600">
                                            {t(
                                              "createShipment.pickupOnlyOtherService"
                                            )}
                                          </p>
                                        )}
                                      </div>
                                    ) : (
                                      <p className="text-xs text-default-500">
                                        {t("createShipment.checkingDelivery")}
                                      </p>
                                    )}
                                    {selectedAttachable.nextBundleFractionOff >
                                      0 && (
                                      <div className="text-xs font-medium text-success">
                                        {t("createShipment.savePickup", {
                                          percent: Math.round(
                                            selectedAttachable.nextBundleFractionOff *
                                              100
                                          ),
                                        })}
                                      </div>
                                    )}
                                    {selectedAttachable.notes && (
                                      <div className="text-xs">
                                        <div className="font-medium text-default-500">
                                          {t("createShipment.notesOnFile")}
                                        </div>
                                        <div className="mt-0.5">
                                          {selectedAttachable.notes}
                                        </div>
                                      </div>
                                    )}
                                    {attachableBookings.length > 1 && (
                                      <Select
                                        size="sm"
                                        aria-label={t(
                                          "createShipment.pickDifferentPickup"
                                        )}
                                        placeholder={t(
                                          "createShipment.pickDifferentPickup"
                                        )}
                                        selectedKeys={
                                          selectedAttachableBookingId
                                            ? [selectedAttachableBookingId]
                                            : []
                                        }
                                        onSelectionChange={(keys) => {
                                          const first = Array.from(
                                            keys as Set<string>
                                          )[0];
                                          if (first)
                                            setSelectedAttachableBookingId(
                                              first
                                            );
                                        }}
                                      >
                                        {attachableBookings.map((b) => (
                                          <SelectItem key={b.id}>
                                            {formatDateInTz(
                                              b.scheduledDate.slice(0, 10),
                                              b.dispatchCenter.timeZone,
                                              "MMM d",
                                              locale
                                            )}
                                            , {b.slot.name}
                                          </SelectItem>
                                        ))}
                                      </Select>
                                    )}
                                  </div>
                                )}
                            </div>
                          </label>

                          <label className="flex items-start gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="pickupMode"
                              className="mt-1"
                              checked={pickupMode === "new"}
                              onChange={() => setPickupMode("new")}
                            />
                            <div className="flex-1">
                              <div className="text-sm font-medium">
                                {t("createShipment.scheduleNewPickup")}
                              </div>
                            </div>
                          </label>
                        </div>
                      )}

                      {pickupMode === "new" && (
                        <div className="space-y-1">
                          <label
                            htmlFor="pickup-tier"
                            className="text-xs font-medium text-default-700"
                          >
                            {t("createShipment.pickupService")}
                          </label>
                          {selectedOption.pickupOptions.length === 1 ? (
                            <div className="text-sm">
                              {
                                selectedOption.pickupOptions[0]
                                  .pickupServiceName
                              }{" "}
                              ·{" "}
                              {formatCurrency(
                                selectedOption.pickupOptions[0].pickupPriceCents
                              )}
                            </div>
                          ) : (
                            <Select
                              id="pickup-tier"
                              aria-label={t("createShipment.pickupService")}
                              placeholder={t(
                                "createShipment.selectPickupService"
                              )}
                              selectedKeys={
                                selectedPickupServiceId
                                  ? [selectedPickupServiceId]
                                  : []
                              }
                              onSelectionChange={(keys) => {
                                const first = Array.from(
                                  keys as Set<string>
                                )[0];
                                if (first) setSelectedPickupServiceId(first);
                              }}
                            >
                              {selectedOption.pickupOptions.map((tier) => (
                                <SelectItem key={tier.pickupServiceId}>
                                  {`${tier.pickupServiceName} · ${formatCurrency(tier.pickupPriceCents)}`}
                                </SelectItem>
                              ))}
                            </Select>
                          )}
                        </div>
                      )}

                      {pickupMode === "new" && (
                        <>
                          <div className="space-y-1">
                            <label
                              htmlFor="pickupDate"
                              className="text-xs font-medium text-default-700"
                            >
                              {t("createShipment.pickupDate")}
                            </label>
                            <Input
                              id="pickupDate"
                              type="date"
                              value={pickupDate}
                              onChange={(e) => setPickupDate(e.target.value)}
                              min={todayIso}
                              {...(maxDateIso ? { max: maxDateIso } : {})}
                            />
                            {dispatchTz && (
                              <p className="text-[11px] text-default-500">
                                {t("createShipment.pickupTimeShown", {
                                  city: dispatchCity
                                    ? ` ${dispatchCity} `
                                    : " ",
                                })}
                              </p>
                            )}
                          </div>
                          <div className="space-y-1.5">
                            <label
                              htmlFor="pickupTimeSlot"
                              className="text-xs font-medium text-default-700"
                            >
                              {t("createShipment.pickupTimeSlot")}
                            </label>
                            <div
                              id="pickupTimeSlot"
                              role="radiogroup"
                              aria-label={t("createShipment.pickupTimeSlot")}
                              className="grid grid-cols-1 gap-1.5"
                            >
                              {bookableSlots.map((slot) => {
                                const range = dispatchTz
                                  ? formatSlotRangeInTz(
                                      slot,
                                      dispatchTz,
                                      pickupDate,
                                      locale
                                    )
                                  : `${slot.startTime} – ${slot.endTime}`;
                                const selected = pickupTimeSlotId === slot.id;
                                const isFull =
                                  slot.availabilityStatus === "FULL" ||
                                  slot.remaining === 0;
                                const incompatible =
                                  slot.availabilityStatus === "AVAILABLE" &&
                                  !slot.estimate;
                                const unavailable =
                                  isFull ||
                                  slot.availabilityStatus !== "AVAILABLE" ||
                                  !slot.estimate;
                                return (
                                  <button
                                    key={slot.id}
                                    type="button"
                                    role="radio"
                                    aria-checked={selected}
                                    disabled={unavailable}
                                    onClick={() => setPickupTimeSlotId(slot.id)}
                                    className={`flex items-center justify-between px-3.5 py-2.5 rounded-medium border text-left transition-colors ${
                                      unavailable
                                        ? "bg-default-50 border-default-200 opacity-60 cursor-not-allowed"
                                        : selected
                                          ? "bg-primary-50 border-primary ring-1 ring-inset ring-primary"
                                          : "bg-content1 border-default-200 hover:border-default-300"
                                    }`}
                                  >
                                    <div className="flex flex-col">
                                      <span
                                        className={`text-sm font-semibold ${selected ? "text-primary-700" : "text-default-900"}`}
                                      >
                                        {range}
                                      </span>
                                      <span className="flex items-center gap-1.5 text-tiny text-default-500">
                                        {slot.name}
                                        <SlotCapacityLabel
                                          remaining={slot.remaining}
                                        />
                                      </span>
                                      {/* Per-slot delivery consequence — lets
                                          the customer pick a slot by outcome. */}
                                      {slot.estimate && dispatchTz && (
                                        <span className="text-tiny text-primary font-medium mt-0.5">
                                          {formatSlotEstimateShort(
                                            slot.estimate,
                                            dispatchTz,
                                            estimateFormat
                                          )}
                                        </span>
                                      )}
                                      {incompatible && (
                                        <span className="text-tiny text-danger-700 mt-0.5">
                                          {estimateReasonText(
                                            slot.estimateReason
                                          )}
                                        </span>
                                      )}
                                      {!isFull &&
                                        slot.availabilityStatus !==
                                          "AVAILABLE" && (
                                          <span className="text-tiny text-danger-700 mt-0.5">
                                            {estimateReasonText(
                                              "NO_BOOKABLE_SLOT"
                                            )}
                                          </span>
                                        )}
                                    </div>
                                    {selected && (
                                      <span className="w-4 h-4 rounded-full bg-primary grid place-items-center">
                                        <span className="w-1.5 h-1.5 rounded-full bg-white" />
                                      </span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                            {dispatchTz && !slotAvailabilityReady ? (
                              <div className="flex items-center gap-2 text-xs text-default-500">
                                <Spinner size="sm" />
                                {t("pickup.when.checking")}
                              </div>
                            ) : slotAvailabilityError ? (
                              <p
                                role="alert"
                                className="text-xs text-danger-700"
                              >
                                {t("createShipment.deliveryCheckFailed")}
                              </p>
                            ) : (
                              bookableSlots.length === 0 &&
                              (slotAvailability?.status === "CLOSED" &&
                              dispatchTz ? (
                                <div
                                  role="status"
                                  className="rounded-medium border border-default-200 bg-default-50 px-3 py-2.5 text-xs text-default-600"
                                >
                                  {slotAvailability.nextAvailableDate
                                    ? t("pickup.closed", {
                                        day: formatDateInTz(
                                          pickupDate,
                                          dispatchTz,
                                          "EEEE",
                                          locale
                                        ),
                                        date: formatDateInTz(
                                          slotAvailability.nextAvailableDate,
                                          dispatchTz,
                                          "EEE, MMM d",
                                          locale
                                        ),
                                      })
                                    : t("pickup.closedNoNext", {
                                        day: formatDateInTz(
                                          pickupDate,
                                          dispatchTz,
                                          "EEEE",
                                          locale
                                        ),
                                      })}
                                </div>
                              ) : (selectedTier?.timeSlots.length ?? 0) > 0 ? (
                                <div
                                  role="alert"
                                  className="flex items-start gap-2 rounded-medium border border-danger-200 bg-danger-50 px-3 py-2.5"
                                >
                                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-danger" />
                                  <div className="flex flex-col">
                                    <span className="text-xs font-semibold text-danger-700">
                                      {t("createShipment.noSlotsLeft")}
                                    </span>
                                    <span className="text-tiny text-danger-600">
                                      {t("createShipment.noSlotsLeftHint")}
                                    </span>
                                  </div>
                                </div>
                              ) : (
                                <p className="text-xs text-default-500">
                                  {t("createShipment.noPickupSlots")}
                                </p>
                              ))
                            )}
                            {slotAvailabilityReady &&
                              selectableSlots.length === 0 && (
                                <div className="space-y-2 text-xs text-default-600">
                                  <p>
                                    {t(
                                      withoutPickupAvailable
                                        ? "createShipment.pickupRecoveryHint"
                                        : "createShipment.pickupOnlyOtherService"
                                    )}
                                  </p>
                                  {withoutPickupAvailable && (
                                    <Button
                                      size="sm"
                                      variant="bordered"
                                      onPress={() => setIncludePickup(false)}
                                    >
                                      {t(
                                        "createShipment.continueWithoutPickup"
                                      )}
                                    </Button>
                                  )}
                                </div>
                              )}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Estimated timeline for the exact new or attached pickup slot. */}
          {(() => {
            if (!selectedOption || !includePickup || !selectedSlotEstimate)
              return null;
            const chosenSlot =
              pickupMode === "attach"
                ? selectedAttachable?.slot
                : bookableSlots.find((slot) => slot.id === pickupTimeSlotId);
            const timelineTimeZone =
              pickupMode === "attach"
                ? selectedAttachable?.dispatchCenter.timeZone
                : dispatchTz;
            if (!chosenSlot || !timelineTimeZone) return null;
            return (
              <DeliveryEstimateTimeline
                estimate={selectedSlotEstimate}
                carrierName={selectedOption.carrier.name}
                pickupDate={effectivePickupDate}
                slotRange={formatSlotRangeInTz(
                  chosenSlot,
                  timelineTimeZone,
                  effectivePickupDate,
                  locale
                )}
                dispatchTz={timelineTimeZone}
              />
            );
          })()}

          {selectedOption &&
            (() => {
              const pickupRaw = includePickup
                ? (selectedTier?.pickupPriceCents ?? 0)
                : 0;
              // Attaching to an existing run earns the bundle discount; the
              // charge applies it server-side at order placement — mirror it
              // here so the preview total matches the card to the cent.
              const fraction =
                includePickup && pickupMode === "attach"
                  ? (selectedAttachable?.nextBundleFractionOff ?? 0)
                  : 0;
              const discount = Math.round(pickupRaw * fraction);
              const taxRaw = includePickup ? (selectedTier?.taxCents ?? 0) : 0;
              // Tax is charged on the NET pickup. Use the exact fraction when
              // the quote provides it (rounded-tax scaling can drift ±1¢).
              const fractionTax = selectedTier?.taxFraction;
              const pickupTax =
                discount === 0
                  ? taxRaw
                  : typeof fractionTax === "number"
                    ? Math.round((pickupRaw - discount) * fractionTax)
                    : pickupRaw > 0
                      ? Math.round(
                          (taxRaw * (pickupRaw - discount)) / pickupRaw
                        )
                      : taxRaw;
              return (
                <PriceSummary
                  option={selectedOption}
                  pickupCents={pickupRaw}
                  pickupDiscountCents={discount}
                  pickupTaxCents={pickupTax}
                />
              );
            })()}
        </aside>
      </div>
    </div>
  );
});
